import { Link, createFileRoute } from "@tanstack/react-router";
import {
  useWatchlist,
  useWatchlistToggle,
  type HydratedWatchlistEntry,
} from "../../lib/watchlist";
import { discoverySource, toMediaItem } from "../../sources/discoverySource";
import { MediaCard } from "../../components/library/media-card";
import { PosterGrid } from "../../components/library/poster-grid";
import { EmptyState, LoadingState } from "../../components/page-state";
import { ErrorNote } from "../../components/ui";
import { BookmarkIcon, XIcon } from "../../components/icons";

export const Route = createFileRoute("/_authed/watchlist")({
  component: Watchlist,
});

function Watchlist() {
  const wl = useWatchlist();
  const { toggle, busyKey, error } = useWatchlistToggle();

  if (wl.isLoading) {
    return <LoadingState />;
  }
  if (wl.isError) {
    return (
      <div className="rago-page mx-auto max-w-xl px-6 pb-24">
        <ErrorNote error={wl.error} />
      </div>
    );
  }

  const all = wl.data?.items ?? [];
  const entries = all.filter((e): e is HydratedWatchlistEntry => e.item !== null);

  if (all.length === 0) {
    return (
      <EmptyState
        icon={<BookmarkIcon size={28} />}
        title="Your watchlist is empty"
        action={
          <Link to="/discover" className="rago-btn rago-btn-fit">
            Browse Discovery
          </Link>
        }
      >
        Save films and shows to watch later and they&apos;ll gather here. Browse Rago Discovery to
        start your list.
      </EmptyState>
    );
  }

  return (
    <div className="rago-page px-6 pb-16 sm:px-10">
      <h1 className="rago-display rago-rise mb-1 text-3xl">Watchlist</h1>
      <p className="rago-rise rago-rise-1 mb-8 text-sm text-[var(--rago-text-3)]">
        {entries.length} title{entries.length === 1 ? "" : "s"}
      </p>
      <ErrorNote error={error} />
      {all.length > entries.length ? (
        <p className="mb-4 text-xs text-[var(--rago-text-3)]">
          Some saved titles couldn&apos;t be loaded right now.
        </p>
      ) : null}
      <PosterGrid>
        {entries.map((e) => {
          const key = `${e.mediaType}:${e.tmdbId}`;
          const busy = busyKey === key;
          return (
            <MediaCard
              key={key}
              item={toMediaItem(e.item)}
              source={discoverySource}
              overlay={
                <button
                  aria-label={`Remove ${e.item.title} from watchlist`}
                  onClick={() => void toggle(e.mediaType, e.tmdbId, true)}
                  disabled={busy}
                  className={`absolute top-2 right-2 z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-[var(--rago-line-1)] bg-[rgba(8,8,10,0.7)] text-sm text-[var(--rago-text-2)] transition-all duration-200 hover:text-[var(--rago-error)] ${
                    busy
                      ? "opacity-100"
                      : "opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
                  }`}
                >
                  {busy ? "…" : <XIcon size={14} />}
                </button>
              }
            />
          );
        })}
      </PosterGrid>
    </div>
  );
}

